<template lang="pug">
  span
    span(@click="modal_open")
      slot(name="activator")
    liondesk-dialog(
      v-model="isModalVisible",
      hide-ok-button,
      dialog-title="Import Contacts - Updated Status",
      cancel-button-text='Close',
      width="1000px"
      @click:cancel='modal_close',
    )
      div(slot='body')
        v-tabs(slider-color="primary" v-model="currentTab")
          v-tab(@click="changeTab('success')")
            span successful
          v-tab(v-if="!hidePartialTab" @click="changeTab('partial')")
            span partially done
          v-tab(@click="changeTab('failed')")
            span failed
          
          v-tab-item
            v-layout.mt-3(row)
              v-flex
                v-card
                  v-data-table.reporting-table(
                    hide-actions
                    :headers="successfulHeaders"
                    :items="contactsReports"
                    :loading="contactsReportsLoading"
                  )
                    template(slot="items" slot-scope="data")
                      tr
                        td 
                          a(:href="'/contacts/'+ data.item.contact_id" target="_blank")
                            | {{ getContactName(data.item.first_name, data.item.last_name) }}
                        td.text-xs-center {{ data.item.email }}
                        td.text-xs-center {{ data.item.created_at | formatDate }}
            v-layout.pagination
              v-flex.pages
                v-pagination(
                  v-if="totalPages > 1"
                  :value="page"
                  :length="totalPages"
                  @input="setPage"
                  :total-visible="7"
                )
              v-flex.limits
                pagination-limit.mr-4(
                  :limits="paginationLimits"
                  :current-limit="limit"
                  @change:limit="changeLimit"
                )
          v-tab-item(v-if="!hidePartialTab")
            v-layout.mt-3(row)
              v-flex
                v-card
                  v-data-table.reporting-table(
                    hide-actions
                    :headers="partialHeaders"
                    :items="contactsReports"
                    :loading="contactsReportsLoading"
                  )
                    template(slot="items" slot-scope="data")
                      tr
                        td 
                          a(:href="'/contacts/'+ data.item.contact_id" target="_blank")
                            | {{ getContactName(data.item.first_name, data.item.last_name) }}
                        td.text-xs-center {{ data.item.email }}
                        td.text-xs-center {{ data.item.file_position }}
                        td.text-xs-center
                          p {{ data.item.error }}
                        td.text-xs-center {{ data.item.created_at | formatDate }}
            v-layout.pagination
              v-flex.pages
                v-pagination(
                  v-if="totalPages > 1"
                  :value="page"
                  :length="totalPages"
                  @input="setPage"
                  :total-visible="7"
                )
              v-flex.limits
                pagination-limit.mr-4(
                  :limits="paginationLimits"
                  :current-limit="limit"
                  @change:limit="changeLimit"
                )
          v-tab-item
            v-layout.mt-3(row wrap)
              v-flex.text-xs-right(v-if="reimportFile" xs12)
                v-btn.mr-0(color="primary" :loading="reimportFileURLLoading" @click="handleDownloadFailedRecords")
                  | Download Records
                  v-icon.ml-1 download
              v-flex(xs12)
                v-card
                  v-data-table.reporting-table(
                    hide-actions
                    :headers="failedHeaders"
                    :items="contactsReports"
                    :loading="contactsReportsLoading"
                    item-key="file_position"
                  )
                    template(slot="headerCell" slot-scope="props")
                      span {{ props.header.text }}
                        v-tooltip(v-if="props.header.showTooltip" top z-index="204")
                          span Click the value to expand
                          template(slot="activator")
                            v-icon.pl-2.tooltip-icon(flat) help_outline
                    template(slot="items" slot-scope="data")
                      tr.failed-records
                        td {{ data.item.file_position }}
                        td.text-xs-center
                          v-chip(@click="data.expanded = !data.expanded" label outline color="grey")
                            span.ma-0.text-none.raw-data {{ data.item.raw_data }}
                        td.text-xs-center {{ data.item.created_at | formatDate }}
                        td.text-xs-center
                          p {{ data.item.error }}
                    template(slot="expand" slot-scope="data")
                      v-card(flat)
                        v-card-text.expanded-raw-data {{ data.item.raw_data }}
            v-layout.pagination
              v-flex.pages
                v-pagination(
                  v-if="totalPages > 1"
                  :value="page"
                  :length="totalPages"
                  @input="setPage"
                  :total-visible="7"
                )
              v-flex.limits
                pagination-limit.mr-4(
                  :limits="paginationLimits"
                  :current-limit="limit"
                  @change:limit="changeLimit"
                )
      template(slot='actions')
        span(v-if="status == 'failed'") If the error persists, please&nbsp;
          a(:href="contactSupportLink") contact support
        v-spacer
        v-btn(flat @click="modal_close") Close
</template>

<script>
import axios from 'axios';
import { mapState, mapActions } from 'vuex';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import PaginationLimit from '@/Core/components/PaginationLimit';
import Modal from '@/Core/mixins/modal';
import Pagination from '@/Core/mixins/pagination';
import { formatDate, formatShortDate } from '@/Core/filters/dates';
import {
  successfulHeaders,
  failedHeaders,
  partialHeaders,
} from '@/Contacts/components/import/config/import-contacts-reports';

const URL = window.URL || window.webkitURL;

export default {
  name: 'ContactsReportModal',
  props: {
    masterId: {
      type: String,
    },
    dateCreated: {
      type: String,
    },
    hidePartialTab: {
      type: Boolean,
    },
    reimportFile: {
      type: Boolean,
    },
  },
  components: {
    LiondeskDialog,
    PaginationLimit,
  },
  mixins: [Modal, Pagination],
  filters: { formatDate },
  data() {
    return {
      currentTab: 0,
      status: 'success',
      page: 1,
      limit: 10,
      successfulHeaders: successfulHeaders,
      failedHeaders: failedHeaders,
      partialHeaders: partialHeaders,
    };
  },
  watch: {
    isModalVisible: {
      handler(value) {
        if (value) {
          this.getContactsRecords();
          return;
        }
        this.page = 1;
        this.status = 'success';
        this.currentTab = 0;
      },
    },
  },
  computed: {
    ...mapState('importContactsReport', [
      'contactsReports',
      'contactsReportsTotal',
      'contactsReportsLoading',
      'reimportFileURL',
      'reimportFileURLLoading',
    ]),
    totalPages() {
      return Math.ceil(this.contactsReportsTotal / this.limit);
    },
    contactSupportLink() {
      const dateFormatted = formatShortDate(this.dateCreated);
      const subject = encodeURIComponent(`Import Contacts Error: Status - Job ID ${this.masterId}`);
      const body = encodeURIComponent(
        `My import contacts job - ID ${this.masterId}, run on ${dateFormatted} - had persistent errors that I have seen in other import contacts reports.`,
      );
      return `mailto:support@liondesk.com?subject=${subject}&body=${body}`;
    },
  },
  methods: {
    ...mapActions('importContactsReport', ['findContactsReports', 'getReimportFileURL']),
    getContactName(first, last) {
      if (first && last) {
        return `${first} ${last}`.trim();
      }

      if (first) {
        return `${first}`.trim();
      }

      if (last) {
        return `${last}`.trim();
      }
      

      return '(Unnamed Contact)';
    },
    changeLimit(newLimit) {
      this.limit = newLimit;
      this.page = 1;
      this.getContactsRecords();
    },
    setPage(newPage) {
      this.page = newPage;
      this.getContactsRecords();
    },
    hasMoreThanOnePage() {
      return this.totalPages > 1;
    },
    changeTab(val) {
      this.status = val;
      this.page = 1;
      this.getContactsRecords();
    },
    getContactsRecords() {
      const params = {
        masterID: this.masterId,
        status: this.status,
        limit: this.limit,
        page: this.page,
      };

      this.findContactsReports(params);
    },
    async handleDownloadFailedRecords() {
      await this.getReimportFileURL({ masterID: this.masterId });

      const link = document.createElement('a');
      link.href = this.reimportFileURL;
      link.setAttribute('download', `failed_records_${this.masterId}.csv`);
      document.body.appendChild(link);
      link.click();
    },
  },
};
</script>

<style lang="scss" scoped>
.reporting-table /deep/ .v-table__overflow {
  overflow-y: scroll;
  max-height: 35vh;
  overflow-x: scroll;
  min-width: inherit;

  tbody td,
  thead tr {
    height: 55px;
  }

  tbody {
    tr:hover {
      box-shadow: none;
    }

    td {
      vertical-align: middle !important;
    }
  }

  thead {
    th {
      position: -webkit-sticky;
      position: sticky;
      background-color: #fff;
      top: 0;
      z-index: 1;
    }
  }
}

.pagination {
  height: 60px;
}
.raw-data {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.expanded-raw-data {
  overflow-wrap: anywhere;
}
.tooltip-icon {
  vertical-align: middle;
}
.tooltip-content {
  z-index: 204 !important;
}
.failed-records /deep/ .v-chip .v-chip__content {
  cursor: pointer;
}
</style>
